async function getRechargeSession() {
  if (window.VerbRecharge?.getSession) {
    return window.VerbRecharge.getSession();
  }

  if (typeof recharge === "undefined") return null;

  try {
    if (!window._RechargeSession) {
      window._RechargeSession = await recharge.auth.loginCustomerPortal();
    }
    return window._RechargeSession;
  } catch (err) {
    console.warn("Recharge portal session unavailable", err);
    return null;
  }
}

async function fetchAccountProfileAddressId() {
  const config = window.VerbRecharge?.getConfig?.();
  if (!config?.proxyPath || !window.VerbRecharge?.isAccountPage?.()) return null;

  try {
    const response = await fetch(`${config.proxyPath}/account-profile`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    const data = await response.json();
    const addresses = data.addresses || [];
    const address = addresses.find((item) => item.default || item.is_default) || addresses[0];
    return address?.id ?? null;
  } catch {
    return null;
  }
}

async function resolvePrimaryAddressId(session) {
  const addressId = await fetchAccountProfileAddressId();
  if (addressId) return addressId;

  const addresses = (
    await recharge.address.listAddresses(session, {
      limit: 1,
    })
  ).addresses;

  return addresses?.[0]?.id ?? null;
}

async function resolveNextChargeDate(session, addressId) {
  const bootstrap = window.__VERB_ACCOUNT_BOOTSTRAP__;
  if (bootstrap?.next_charge?.scheduled_at) {
    return bootstrap.next_charge.scheduled_at;
  }

  const chargesResponse = await recharge.charge.listCharges(session, {
    status: "queued",
    address_id: addressId,
  });

  if (chargesResponse.charges?.length) {
    return chargesResponse.charges[0].scheduled_at;
  }

  const subscriptions = (
    await recharge.subscription.listSubscriptions(session, {
      address_id: addressId,
      status: "active",
    })
  ).subscriptions;

  if (subscriptions?.length) {
    return subscriptions[0].next_charge_scheduled_at;
  }

  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  return nextMonth.toISOString().split("T")[0];
}

async function addProductToNextOrder(product, button) {
  const ui = window.VerbAccountUI;
  ui?.setButtonLoading(button, true, { loadingLabel: "Adding…" });

  try {
    const session = await getRechargeSession();
    if (!session) {
      throw new Error("Unable to connect to your subscription account. Please refresh and try again.");
    }

    const addressId = await resolvePrimaryAddressId(session);
    if (!addressId) {
      throw new Error("No shipping address found for your subscription.");
    }

    const nextChargeDate = await resolveNextChargeDate(session, addressId);

    await recharge.onetime.createOnetime(session, {
      address_id: addressId,
      next_charge_scheduled_at: nextChargeDate,
      quantity: 1,
      external_variant_id: {
        ecommerce: String(product.variant_id),
      },
      external_product_id: {
        ecommerce: String(product.id),
      },
      product_title: product.title,
    });

    ui?.setButtonLoading(button, false);
    ui?.showButtonSuccess(button, "Added!");
    document.dispatchEvent(new CustomEvent("Affinity:refresh"));
  } catch (error) {
    ui?.setButtonLoading(button, false);
    throw error;
  }
}

class RpInfoCards extends HTMLElement {
  constructor() {
    super();

    this.swiperEl = this.querySelector(".swiper");
  }

  connectedCallback() {
    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: 1.05,
      spaceBetween: 12,
      centeredSlides: true,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
        releaseOnEdges: true,
      },
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      breakpoints: {
        480: {
          slidesPerView: 1.5,
          pagination: {
            el: ".swiper-pagination",
            clickable: true,
          },
        },
        768: {
          slidesPerView: "auto",
          pagination: false,
          spaceBetween: 8,
          centeredSlides: false,
        },
      },
    });
  }
}

class RpNextOrderUpsell extends HTMLElement {
  constructor() {
    super();

    this.atcBtn = this.querySelector(".js-atc-btn");
    this.product = JSON.parse(this.dataset.productData);
  }

  connectedCallback() {
    this.addEventListener("click", this._handleAtcBtnClick.bind(this));
  }

  async _handleAtcBtnClick(event) {
    const button = event.target.closest(".js-atc-btn") || this.atcBtn;
    if (!button || button.disabled) return;

    try {
      await addProductToNextOrder(this.product, button);
    } catch (error) {
      console.error("Add to next order failed:", error);
      window.alert(error.message || "Unable to add this item to your next order.");
    }
  }
}

class RpRecommendations extends HTMLElement {
  constructor() {
    super();

    this.swiperEl = this.querySelector(".swiper");
  }

  connectedCallback() {
    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: "auto",
      spaceBetween: 20,
      mousewheel: {
        forceToAxis: true,
        sensitivity: 1,
        releaseOnEdges: true,
      },
    });
  }
}

class RpProductCard extends HTMLElement {
  constructor() {
    super();

    this.atcBtn = this.querySelector(".js-atc-btn");
    this.product = JSON.parse(this.dataset.productData);
  }

  connectedCallback() {
    this.addEventListener("click", this._handleAtcBtnClick.bind(this));
  }

  async _handleAtcBtnClick(event) {
    const button = event.target.closest(".js-atc-btn") || this.atcBtn;
    if (!button || button.disabled) return;

    try {
      await addProductToNextOrder(this.product, button);
    } catch (error) {
      console.error("Add to next order failed:", error);
      window.alert(error.message || "Unable to add this item to your next order.");
    }
  }
}

customElements.define("rp-info-cards", RpInfoCards);
customElements.define("rp-next-order-upsell", RpNextOrderUpsell);
customElements.define("rp-recommendations", RpRecommendations);
customElements.define("rp-product-card", RpProductCard);
